/**
 * « Mes chiffres » de Mon compte v2 (SPEC MC5, règles § 3.6 ; retours de Daphné du 07/10).
 *
 * Le compteur REPREND À L'IDENTIQUE le calcul de la v1 (screens/Account/UserProfile.tsx,
 * Statistics()) : mêmes exclusions, même dédoublonnage, même max(Mindbody, Stretch) par site. Critère
 * SPEC MC5 : le total v2 = le total de la v1 pour le même compte (test : stats.test.ts).
 * Ajouts : « Cette année » (SPEC MC5), une carte par marque en tête (brandCards : retours du 07/10, demande de Luc
 * du 08/10 ; marques sans cours masquées depuis le 09/10, shownBrandCards), total et année au second plan
 * (secondaryStats).
 *
 * Studio et coach préférés (retour du 07/10, « studio préféré : La Défense (33 sessions) alors que je
 * n'ai fait aucun cours là-bas ») : la v1 comptait TOUTES les visites du site courant, dont les « Cross
 * Region Visit » (VisitType -1). Ce sont les écritures que Mindbody pose sur le site dont le forfait
 * (ou le contrat) a payé un cours pris dans une autre marque : Id de lieu 1 et Id de coach 1 quel que
 * soit le studio réel (constaté le 07/10 en lecture sur un compte de la prod Dynamo Paris : 33 écritures
 * -1, lieu 1, coach 1, ClassId 0 ; le lieu 1 de Dynamo Paris est La Défense). Les Id de lieu et de coach sont en
 * outre propres à chaque site Mindbody (lieu 1 = La Défense chez Dynamo, Charlot chez RIISE,
 * Champs-Élysées chez Punch). Désormais : seulement les séances comptées par le compteur
 * (isCountedVisit, sans les « Converted Visit » de l'import Zingfit, dont le lieu n'est pas fiable),
 * toutes marques de la région, clé (site, lieu) ou (site, coach) d'un bout à l'autre.
 */
import type { ClientVisit } from 'types/client';
import type { Staff } from 'types/staff';
import type { MindbodyLocation } from 'types/studio';
import { sourceFailed, sourcePending, type QuerySource } from './query';

export interface VisitStats {
  /** Compteur par site Mindbody (clé = SiteId en chaîne), Mindbody réalisé + historique Zingfit. */
  bySite: Record<string, number>;
  /** Somme des compteurs par site (= « cours réalisés chez Monday » de la v1). */
  total: number;
  /** Cours réalisés pendant l'année civile en cours (visites datées seulement). */
  thisYear: number;
}

const CONVERTED_VISIT = 'Converted Visit';

/**
 * Séance comptée (= UserProfile.tsx Statistics() `countedVisits`, règle du programme de fidélité) : ni
 * annulation tardive (LateCancelled), ni absence (Missed), ni « Cross Region Visit » (VisitType -1,
 * doublon posé sur le site qui a payé : la vraie visite existe sur le site du cours), ni cours à venir,
 * ni visite hors région (`regionalSiteIds`, absent ou vide = pas de filtre).
 */
export function isCountedVisit(visit: ClientVisit, regionalSiteIds: number[] | undefined, nowTime: number): boolean {
  return (
    !visit.LateCancelled &&
    !visit.Missed &&
    visit.VisitType !== -1 &&
    new Date(visit.StartDateTime).getTime() <= nowTime &&
    (!regionalSiteIds?.length || regionalSiteIds.includes(Number(visit.SiteId)))
  );
}

/**
 * = UserProfile.tsx Statistics() : `countedVisits`, `visitsBySite`, `totalVists`.
 *
 * Les « Converted Visit » (import Zingfit de Mindbody) sont dédoublonnées (même site, client, début,
 * fin) puis mises en concurrence, par site, avec l'historique Zingfit de Stretch : on garde le plus
 * grand des deux.
 */
export function computeVisitStats(
  visits: ClientVisit[] | undefined,
  zingfitVisits: Record<string, number> | undefined,
  regionalSiteIds: number[] | undefined,
  now: Date = new Date(),
): VisitStats {
  const nowTime = now.getTime();

  // = countedVisits
  const countedVisits = visits?.filter((visit) => isCountedVisit(visit, regionalSiteIds, nowTime));

  // = visitsBySite
  const counts: { [key: string]: number } = {};
  const convertedBySite: { [key: string]: number } = {};
  const seenConverted = new Set<string>();
  const uniqueVisits = countedVisits?.filter((visit) => {
    if (visit.Name !== CONVERTED_VISIT) {
      return true;
    }
    const key = `${visit.SiteId}|${visit.ClientId}|${visit.StartDateTime}|${visit.EndDateTime}`;
    if (seenConverted.has(key)) {
      return false;
    }
    seenConverted.add(key);
    return true;
  });

  uniqueVisits?.forEach((visit) => {
    const siteId = String(visit.SiteId);
    if (visit.Name === CONVERTED_VISIT) {
      convertedBySite[siteId] = (convertedBySite[siteId] || 0) + 1;
    } else {
      counts[siteId] = (counts[siteId] || 0) + 1;
    }
  });

  const siteIdsWithHistory = new Set([...Object.keys(convertedBySite), ...Object.keys(zingfitVisits ?? {})]);
  siteIdsWithHistory.forEach((siteId) => {
    if (regionalSiteIds?.length && !regionalSiteIds.includes(Number(siteId))) {
      return;
    }
    const fromMindbody = convertedBySite[siteId] || 0;
    const fromStretch = zingfitVisits?.[siteId];
    counts[siteId] =
      (counts[siteId] || 0) + (fromStretch === undefined ? fromMindbody : Math.max(fromMindbody, fromStretch));
  });

  // = totalVists
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0);

  // Ajout v2 : mêmes visites comptées (dédoublonnées), datées de l'année civile en cours. L'historique
  // Zingfit de Stretch n'a pas de date : il n'entre pas dans « Cette année » (il est d'avant 2024).
  const year = now.getFullYear();
  const thisYear = (uniqueVisits ?? []).filter((visit) => new Date(visit.StartDateTime).getFullYear() === year).length;

  return { bySite: counts, total, thisYear };
}

/** Compteur d'une marque (= d'un site Mindbody) pour « Mes chiffres ». */
export interface BrandCount {
  siteId: number;
  count: number;
}

/**
 * Marques de « Mes chiffres » (retours du 07/10 et demande de Luc du 08/10 : « mettre chacune des marques plus en
 * avant » ; le programme de fidélité est par marque) : chaque marque de la ville (`citySiteIds` :
 * mondayMindbody.uxV2.brands, dans leur ordre), plus tout site de la région où le membre a des cours sans y figurer.
 * Ordre :
 * 1. la marque du site visité, toujours, même sans cours (c'est sa page) ;
 * 2. les autres marques où le membre a des cours, du plus grand compteur au plus petit (à égalité, ordre de la ville) ;
 * 3. les marques de la ville sans cours, dans l'ordre de la ville.
 * Le site visité n'est ajouté que s'il est connu (Id non vide). Cartes affichées : shownBrandCards (marques sans cours
 * masquées depuis le 09/10) ; aucune quand les chiffres sont indisponibles (statsStatus, Stats.tsx).
 */
export function brandCards(
  bySite: Record<string, number>,
  citySiteIds: Array<number | string>,
  currentSiteId: string | number | undefined,
): BrandCount[] {
  const current = Number(currentSiteId);
  const order: number[] = [];
  const add = (siteId: number) => {
    if (Number.isFinite(siteId) && siteId > 0 && !order.includes(siteId)) order.push(siteId);
  };
  add(current);
  citySiteIds.forEach((siteId) => add(Number(siteId)));
  Object.entries(bySite).forEach(([siteId, count]) => {
    if (count > 0) add(Number(siteId));
  });

  const rank = (siteId: number) => order.indexOf(siteId);
  const cards = order.map((siteId) => ({ siteId, count: Math.max(0, bySite[String(siteId)] ?? 0) }));
  return cards.sort((a, b) => {
    const aCurrent = a.siteId === current ? 1 : 0;
    const bCurrent = b.siteId === current ? 1 : 0;
    if (aCurrent !== bCurrent) return bCurrent - aCurrent;
    const aActive = a.count > 0 ? 1 : 0;
    const bActive = b.count > 0 ? 1 : 0;
    return bActive - aActive || b.count - a.count || rank(a.siteId) - rank(b.siteId);
  });
}

/**
 * Cartes AFFICHÉES de « Mes chiffres », parmi celles de brandCards (question de Luc du 09/10 : « Quand pas de cours, on
 * affiche pas le bloc peut-être sinon ça va faire vide ? »). La rubrique montre ce que le membre a fait, pas ce qu'il
 * n'a pas fait ; la découverte des autres marques reste au planning (filtre Marque).
 * - Des cours dans au moins une marque : ces marques seules, dans l'ordre de brandCards (marque du site d'abord si elle
 *   en a, puis du plus grand compteur au plus petit).
 * - Aucun cours nulle part (nouveau membre) : une seule carte, la première de brandCards (la marque du site), sobre,
 *   qui invite à réserver (« Pas encore de cours », lien vers le planning).
 * Pendant le chargement (brandCards sans compteur), même carte : le squelette a la forme la plus probable (une marque).
 */
export function shownBrandCards(cards: BrandCount[]): BrandCount[] {
  const withClasses = cards.filter((card) => card.count > 0);
  return withClasses.length ? withClasses : cards.slice(0, 1);
}

/**
 * Chiffres d'ensemble, au second plan sous les cartes (demande de Luc du 08/10 : « le nombre "tout Monday" et par
 * année sont moins importants »). « Tout Monday » seulement quand les cours sont répartis sur au moins deux marques :
 * sur une seule, il répète le chiffre de sa carte. « Cette année » seulement avec au moins un cours cette année (même
 * règle que les cartes, question de Luc du 09/10 : on ne montre pas un 0). Rien sans aucun cours réalisé.
 */
export function secondaryStats(stats: VisitStats): { total: number | null; thisYear: number | null } | null {
  if (stats.total <= 0) return null;
  const brandsWithClasses = Object.values(stats.bySite).filter((count) => count > 0).length;
  return { total: brandsWithClasses > 1 ? stats.total : null, thisYear: stats.thisYear > 0 ? stats.thisYear : null };
}

export type StatsStatus = 'pending' | 'unavailable' | 'ready';

/**
 * État de « Mes chiffres » d'après ses deux sources, GET client/visits et client/zingfit-visits?strict=1 (historique
 * Stretch ; hooks.ts useStrictZingfitVisits) ; règles des lectures dans lib/query.ts.
 * - `unavailable` (décision de DA du 09/10) : une des deux lectures a échoué (erreur, ou pause hors ligne) et rien
 *   n'est en cache. Le compteur serait faux (« 0 · Pas encore de cours » pour un membre fidèle ; visites sans
 *   l'historique Stretch pour un ancien membre Zingfit) : la rubrique le dit, sans attendre l'autre source.
 * - `pending` : une des deux lectures n'a encore rien reçu (relecture du 09/10).
 * - `ready` : les deux sont là. Une nouvelle lecture en erreur ou en pause avec des données en cache garde le cache.
 */
export function statsStatus(visits: QuerySource, zingfit: QuerySource): StatsStatus {
  if (sourceFailed(visits) || sourceFailed(zingfit)) return 'unavailable';
  if (sourcePending(visits) || sourcePending(zingfit)) return 'pending';
  return 'ready';
}

/**
 * Studio ou coach préféré affiché ? (appliqué à chacun des deux, Stats.tsx). Hors indisponibilité : dès qu'il existe (il
 * ne vient que des visites, même en attente de l'historique Stretch). Chiffres indisponibles (décision de DA du 09/10 :
 * pas de préférés) : l'état d'avant est figé. Aucun n'apparaît, même si les visites, les lieux ou les coachs arrivent
 * entre-temps ; ceux déjà affichés pendant l'attente (visites en cache, historique en retard puis en erreur) restent :
 * les retirer ferait remonter toute la suite de la page (369 px à 360 px). Rien ne bouge entre l'attente et l'erreur,
 * dans aucun ordre d'arrivée.
 */
export function favoritesShown(shownBefore: boolean, status: StatsStatus, hasFavorites: boolean): boolean {
  return status === 'unavailable' ? shownBefore : hasFavorites;
}

/**
 * Nombre de séances du studio préféré (« Lafayette (10) ») affiché ? Pas pendant l'indisponibilité (relecture du 09/10) :
 * sous « Chiffres indisponibles pour le moment », un préféré gardé de l'attente ne montre plus aucun chiffre. Le nombre
 * reste à sa place, invisible (Stats.tsx, account.css) : la ligne ne change ni de hauteur ni de coupure.
 */
export function favoriteCountShown(status: StatsStatus): boolean {
  return status !== 'unavailable';
}

interface Ranked {
  siteId: number;
  id: number;
  count: number;
  last: number;
}

/**
 * Séances comptées (isCountedVisit, sans « Converted Visit ») regroupées par (site, Id), de la plus
 * fréquente à la moins fréquente ; à égalité, la plus récente d'abord.
 */
function rankBy(
  visits: ClientVisit[] | undefined,
  idOf: (visit: ClientVisit) => number | undefined | null,
  regionalSiteIds: number[] | undefined,
  now: Date,
): Ranked[] {
  const nowTime = now.getTime();
  const groups = new Map<string, Ranked>();
  (visits ?? []).forEach((visit) => {
    if (visit.Name === CONVERTED_VISIT || !isCountedVisit(visit, regionalSiteIds, nowTime)) return;
    const id = Number(idOf(visit));
    const siteId = Number(visit.SiteId);
    if (!id || !siteId) return;
    const key = `${siteId}:${id}`;
    const time = new Date(visit.StartDateTime).getTime() || 0;
    const group = groups.get(key);
    if (group) {
      group.count += 1;
      group.last = Math.max(group.last, time);
    } else {
      groups.set(key, { siteId, id, count: 1, last: time });
    }
  });
  return [...groups.values()].sort((a, b) => b.count - a.count || b.last - a.last);
}

export interface PreferredStudio {
  location: MindbodyLocation;
  siteId: number;
  count: number;
}

/**
 * Studio le plus fréquenté, toutes marques de la région, clé (site, lieu) : le lieu est cherché dans
 * GET site/locations (lieux de chaque site de la région, champ SiteID). Premier groupe dont le lieu est
 * connu (un lieu sans cours n'est pas listé).
 */
export function preferredStudio(
  visits: ClientVisit[] | undefined,
  studios: MindbodyLocation[] | undefined,
  regionalSiteIds: number[] | undefined,
  now: Date = new Date(),
): PreferredStudio | null {
  for (const group of rankBy(visits, (visit) => visit.LocationId, regionalSiteIds, now)) {
    const location = studios?.find((studio) => Number(studio.Id) === group.id && Number(studio.SiteID) === group.siteId);
    if (location) return { location, siteId: group.siteId, count: group.count };
  }
  return null;
}

export interface PreferredCoach {
  coach: Staff;
  siteId: number;
  count: number;
}

/** Coach le plus vu, toutes marques de la région, clé (site, coach) : coach cherché dans GET staff/coaches. */
export function preferredCoach(
  visits: ClientVisit[] | undefined,
  coaches: Staff[] | undefined,
  regionalSiteIds: number[] | undefined,
  now: Date = new Date(),
): PreferredCoach | null {
  for (const group of rankBy(visits, (visit) => visit.StaffId, regionalSiteIds, now)) {
    const coach = coaches?.find((c) => Number(c.Id) === group.id && Number(c.SiteId) === group.siteId);
    if (coach) return { coach, siteId: group.siteId, count: group.count };
  }
  return null;
}
